'use client';

import * as React from 'react';
import Link from 'next/link';
import { 
  AppBar as MuiAppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Divider, 
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { useTheme, useMediaQuery } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBadge from '../notifications/NotificationBadge';

interface AppBarProps {
  onMenuClick: () => void;
}

export default function AppBar({ onMenuClick }: AppBarProps) {
  const router = useRouter();
  const { user, logout, isLoading: isAuthLoading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [hasHydrated, setHasHydrated] = React.useState(false);

  React.useEffect(() => {
    setHasHydrated(true);
  }, []);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (isMobile) {
      // For mobile, we'll show the menu at the bottom
      setAnchorEl(document.body);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    router.push('/login');
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    router.push('/');
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const safeUser = hasHydrated && !isAuthLoading ? user : null;
  const userInitial = safeUser?.name ? safeUser.name.charAt(0).toUpperCase() : 'U';
  const userName = safeUser?.name || 'User';
  const userRole = safeUser?.role || 'User Role';
  const userEmail = safeUser?.email || 'user@example.com';

  return (
    <MuiAppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{ 
            mr: 2,
            color: 'text.primary',
          }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            background: 'linear-gradient(45deg, #EB0A1E, #C4081A)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block',
          }}
        >
          <Link href="/" aria-label="Ir al inicio" style={{ display: 'inline-flex' }}>
            <Box component="img" src="/jidoka-black.png" alt="Jidoka" sx={{ height: 40 }} />
          </Link>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <NotificationBadge userId={safeUser?.id || ''} />

          {/* User Menu */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={handleProfileMenuOpen}
          >
            <Avatar 
              sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" lineHeight={1.2}>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1}>
                {userRole}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={isMobile ? null : anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                width: isMobile ? '100%' : 200,
                maxWidth: 'none',
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  '&:not(:last-child)': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                },
                [theme.breakpoints.down('sm')]: {
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  maxWidth: '100%',
                  m: 0,
                  borderRadius: '16px 16px 0 0',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 8,
                    left: '50%',
                    width: 40,
                    height: 4,
                    backgroundColor: theme.palette.divider,
                    borderRadius: 2,
                    transform: 'translateX(-50%)',
                  },
                },
              },
            }}
            disableScrollLock={true}
            disablePortal={isMobile}
            transformOrigin={{
              vertical: isMobile ? 'bottom' : 'top',
              horizontal: isMobile ? 'center' : 'right',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'right',
            }}
            MenuListProps={{
              'aria-labelledby': 'user-menu-button',
              sx: { 
                py: isMobile ? 2 : 0,
                '&:focus': {
                  outline: 'none',
                },
              },
            }}
          >
            <Box sx={{ 
              px: 2, 
              py: 1.5, 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              [theme.breakpoints.down('sm')]: {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
            }}>
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 600,
                  display: isMobile ? 'flex' : 'none',
                }}
              >
                {userInitial}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {userName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {userEmail}
                </Typography>
              </Box>
            </Box>
            <Link href="/profile" passHref>
              <MenuItem>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Perfil</ListItemText>
              </MenuItem>
            </Link>
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Configuración</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
}
